'use client';

import { useAuthStore } from '@/store/authStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { LoginRequest, AdminLoginRequest } from '@/services/api';
import type { User } from '@/types';

/**
 * 인증 관련 커스텀 훅
 * - 로그인/로그아웃 상태 관리
 * - 사용자 정보 조회
 * - 인증 상태 확인
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    setUser,
    setError,
    updateUser,
    clearError,
  } = useAuthStore();

  const queryClient = useQueryClient();

  // 현재 사용자 정보 조회 (비활성화 - 로그인 시 이미 저장됨)
  // const { data: currentUser, isLoading: isUserLoading } = useQuery({
  //   queryKey: ['auth', 'me'],
  //   queryFn: authApi.getCurrentUser,
  //   enabled: isAuthenticated,
  //   retry: false,
  // });

  // Admin 로그인 뮤테이션
  const adminLoginMutation = useMutation({
    mutationFn: (data: AdminLoginRequest) => authApi.adminLogin(data),
    onSuccess: (response) => {
      // Admin 로그인 성공 시 사용자 정보를 스토어에 저장
      const user = {
        id: response.adminSeq.toString(),
        email: response.adminId,
        name: response.adminName,
        role: response.adminType.toLowerCase(),
      };
      
      // 스토어에 사용자 정보 저장
      setUser(user);
      queryClient.setQueryData(['auth', 'me'], user);
    },
    onError: (error: any) => {
      setError(error.message || '로그인에 실패했습니다.');
    },
  });

  // 일반 로그인 뮤테이션 (기존 호환성)
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response, variables) => {
      login(variables.email, variables.password);
      queryClient.setQueryData(['auth', 'me'], response.user);
    },
    onError: (error: any) => {
      setError(error.message || '로그인에 실패했습니다.');
    },
  });

  // 로그아웃 뮤테이션
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      logout();
      queryClient.clear();
    },
    onError: () => {
      // 에러가 발생해도 로컬 상태는 초기화
      logout();
      queryClient.clear();
    },
  });

  // 토큰 갱신 뮤테이션
  const refreshTokenMutation = useMutation({
    mutationFn: authApi.refreshToken,
    onSuccess: () => {
      // 토큰이 자동으로 localStorage에 저장됨
      console.log('토큰 갱신 성공');
    },
    onError: (error) => {
      console.error('토큰 갱신 실패:', error);
      // 토큰 갱신 실패 시 로그아웃
      logout();
      queryClient.clear();
    },
  });

  // 프로필 업데이트 뮤테이션
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<User>) => authApi.updateProfile(data),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      queryClient.setQueryData(['auth', 'me'], updatedUser);
    },
    onError: (error: any) => {
      setError(error.message || '프로필 업데이트에 실패했습니다.');
    },
  });

  // 비밀번호 변경 뮤테이션
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onError: (error: any) => {
      setError(error.message || '비밀번호 변경에 실패했습니다.');
    },
  });

  return {
    // 상태
    user: user,
    isAuthenticated,
    isLoading: isLoading,
    error,

    // 액션
    login: loginMutation.mutate,
    adminLogin: adminLoginMutation.mutate,
    logout: logoutMutation.mutate,
    refreshToken: refreshTokenMutation.mutate,
    updateProfile: updateProfileMutation.mutate,
    changePassword: changePasswordMutation.mutate,
    clearError,

    // 뮤테이션 상태
    isLoggingIn: loginMutation.isPending || adminLoginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRefreshingToken: refreshTokenMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,

    // 뮤테이션 에러
    loginError: loginMutation.error || adminLoginMutation.error,
    logoutError: logoutMutation.error,
    refreshTokenError: refreshTokenMutation.error,
    updateProfileError: updateProfileMutation.error,
    changePasswordError: changePasswordMutation.error,

    // 뮤테이션 객체 (디버깅용)
    adminLoginMutation,
    refreshTokenMutation,
  };
}
